// src/services/presenceVisibility.js
// ─────────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for "who can see my Last Seen / Online", ported
// rule-for-rule from the app (src/utils/presenceVisibility.ts).
//
// Both display sites here — the chat-list green dot and the chat header — MUST
// go through `derivePresenceVisibility`, for the same reason the app insists on
// it: two copies of a privacy gate drift, and a gate that drifts is a leak.
//
// Semantics:
//   TARGET's `lastSeen` decides who may see the target's last seen:
//     'everyone'    → anyone
//     'my_contacts' → only people in the TARGET's contacts
//     'except'      → everyone EXCEPT the uids in `lastSeenExcept`
//     'nobody'      → no one
//   TARGET's `online`:
//     'everyone'          → online visible to anyone, even if last seen is hidden
//     'same_as_last_seen' → online inherits the last-seen decision
//   PER-CHAT overrides (chats/{id}.lastSeenBy[targetUid] / onlineBy[targetUid])
//     are the target's switches toward THIS viewer and WIN over the global —
//     except reciprocity, which is a hard viewer-side block.
//   RECIPROCITY: a viewer who shares their own last seen with 'nobody' sees
//     nobody else's last seen or online. That is the promise the app's privacy
//     notice makes, so the web has to keep it too.

import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export function derivePresenceVisibility({
  viewerPrivacy, targetPrivacy, viewerUid, isContact, perChatLastSeen, perChatOnline,
} = {}) {
  const vp = viewerPrivacy || {};
  const tp = targetPrivacy || {};
  const uid = viewerUid || '';

  // reciprocity — a hard block that even a per-chat "on" cannot lift
  if (vp.lastSeen === 'nobody') return { canSeeLastSeen: false, canSeeOnline: false };

  const ls = tp.lastSeen || 'everyone';
  let globalLastSeen;
  if (ls === 'nobody') globalLastSeen = false;
  else if (ls === 'my_contacts') globalLastSeen = !!isContact;
  else if (ls === 'except') globalLastSeen = !((tp.lastSeenExcept || []).includes(uid));
  else globalLastSeen = true;

  const canSeeLastSeen = (perChatLastSeen === true || perChatLastSeen === false)
    ? perChatLastSeen
    : globalLastSeen;

  const on = tp.online || 'same_as_last_seen';
  const canSeeOnline = (perChatOnline === true || perChatOnline === false)
    ? perChatOnline
    : (on === 'everyone' ? true : canSeeLastSeen);

  return { canSeeLastSeen, canSeeOnline };
}

/* ── "is the viewer in the target's contacts?" — only asked when the target's
      setting is 'my_contacts', so the common path stays free. Cached briefly so
      a chat list doesn't re-read it per render. ─────────────────────────────── */
const _cache = new Map();
const TTL = 60_000;

export async function isViewerInTargetContacts(targetUid, viewerUid) {
  if (!targetUid || !viewerUid) return false;
  const now = Date.now();
  let entry = _cache.get(targetUid);
  if (!entry || now - entry.at > TTL) {
    try {
      const snap = await getDoc(doc(db, 'userConnections', targetUid));
      const conns = snap.exists() ? (snap.data().connections || []) : [];
      const has = new Set();
      conns.forEach((c) => { if (c?.uid) has.add(c.uid); if (c?.id) has.add(c.id); });
      entry = { has, at: now };
    } catch {
      entry = { has: new Set(), at: now };
    }
    _cache.set(targetUid, entry);
  }
  return entry.has.has(viewerUid);
}

export function invalidateContactsCache(targetUid) {
  if (targetUid) _cache.delete(targetUid); else _cache.clear();
}

/* ── the per-chat switches I own toward this person ────────────────────────── */
export const setChatLastSeenShared = (chatId, uid, on) =>
  updateDoc(doc(db, 'chats', chatId), { [`lastSeenBy.${uid}`]: !!on });

export const setChatOnlineShared = (chatId, uid, on) =>
  updateDoc(doc(db, 'chats', chatId), { [`onlineBy.${uid}`]: !!on });

/* ── the global privacy settings (users/{uid}.privacySettings) ─────────────── */
export const DEFAULT_PRIVACY = {
  lastSeen: 'everyone',
  online: 'same_as_last_seen',
  lastSeenExcept: [],
  readReceipts: true,
};

export const setGlobalPrivacy = (uid, patch) => updateDoc(doc(db, 'users', uid), Object.fromEntries(
  Object.entries(patch).map(([k, v]) => [`privacySettings.${k}`, v]),
));
